import { CommonModule, DatePipe, DecimalPipe } from '@angular/common';
import {
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Subscription, filter, fromEvent, timer } from 'rxjs';

import { ConfirmModalComponent } from '../../../shared/components/ui/confirm-modal/confirm-modal.component';
import { DataStateComponent } from '../../../shared/components/ui/data-state/data-state.component';
import { ModalComponent } from '../../../shared/components/ui/modal/modal.component';
import { PageHeaderComponent } from '../../../shared/components/ui/page-header/page-header.component';
import { toErrorMessage } from '../../../shared/helpers/http-error';
import { DepositService } from '../../../shared/services/deposit.service';
import { PoliciesService } from '../../../shared/services/policies.service';
import { VehiclesService } from '../../../shared/services/vehicles.service';
import { DepositEffectiveRateResponse } from '../../../shared/types/deposit.types';
import { CancellationPolicy } from '../../../shared/types/policy.types';
import {
  FUEL_TYPE_LABEL,
  GEARBOX_LABEL,
  VEHICLE_STATUS_META,
  Vehicle,
  VehicleStatus,
} from '../../../shared/types/vehicle.types';
import { VehicleAvailabilityComponent } from '../vehicle-availability/vehicle-availability.component';
import { VehicleOptionsComponent } from '../vehicle-options/vehicle-options.component';
import { VehiclePhotosComponent } from '../vehicle-photos/vehicle-photos.component';
import { TPipe } from '../../../shared/pipe/t.pipe';
import { LocalizedDatePipe } from '../../../shared/pipe/localized-date.pipe';
import { LocaleService } from '../../../shared/services/locale.service';
import { serverDate } from '../../../shared/helpers/dates';

@Component({
  selector: 'app-vehicle-detail',
  imports: [CommonModule, RouterModule, DatePipe, DecimalPipe, PageHeaderComponent, DataStateComponent, ConfirmModalComponent, ModalComponent, VehicleAvailabilityComponent, VehicleOptionsComponent, VehiclePhotosComponent, TPipe, LocalizedDatePipe],
  templateUrl: './vehicle-detail.component.html',
})
export class VehicleDetailComponent implements OnInit {
  private readonly locale = inject(LocaleService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly vehiclesService = inject(VehiclesService);
  private readonly policiesService = inject(PoliciesService);
  private readonly depositService = inject(DepositService);
  private readonly destroyRef = inject(DestroyRef);

  /** Intervalle de polling de `/link-contract/{id}` pendant que la
   *  popup Docuseal est ouverte (ms). */
  private static readonly SIGN_POLL_MS = 3000;

  readonly vehicle = signal<Vehicle | null>(null);
  readonly loading = signal(true);
  readonly loadError = signal<string | null>(null);

  /** Politique d'annulation effective (override véhicule sinon globale, §C2). */
  readonly effectivePolicy = signal<CancellationPolicy | null>(null);
  /** Taux de caution effectif du véhicule (#46). */
  readonly depositRate = signal<DepositEffectiveRateResponse | null>(null);

  // ── Contract signing flow (Docusign / Docuseal) ──────────────────────
  readonly signOpen = signal(false);
  readonly signRefreshing = signal(false);
  readonly signError = signal<string | null>(null);

  /** Transitions de statut owner (via /me/change-status). */
  readonly publishing = signal(false);
  readonly publishError = signal<string | null>(null);

  // delete modal (self-service)
  readonly deleteOpen = signal(false);
  readonly deleting = signal(false);
  readonly deleteError = signal<string | null>(null);

  readonly statusMeta = VEHICLE_STATUS_META;
  readonly fuelLabel = FUEL_TYPE_LABEL;
  readonly gearLabel = GEARBOX_LABEL;

  readonly displayName = computed(() => {
    const v = this.vehicle();
    if (!v) return '';
    const make = v.make?.entityCode ?? v.make?.name ?? '';
    const model = v.model?.entityCode ?? v.model?.name ?? '';
    return [make, model].filter(Boolean).join(' ') || this.locale.t('label.vehicleNum', { id: v.id });
  });

  /** Une date ISO est-elle déjà passée ? (colore les dates d'expiration, #55). */
  isExpired(date?: string | null): boolean {
    return date ? serverDate(date).getTime() < Date.now() : false;
  }

  // ── Matrice des transitions owner ─────────────────────────────────────
  // Alignée sur VehicleService.changeVehicleStatus côté back :
  //   AVAILABLE         → UNDER_MAINTENANCE | UNAVAILABLE
  //   UNDER_MAINTENANCE → AVAILABLE
  //   UNAVAILABLE       → AVAILABLE
  //   PRE_UNDER_REVIEW  → UNDER_REVIEW            (resoumission après rejet)
  //   default           → interdit côté owner
  /** Le contrat de mise à disposition n'est pas (encore) signé. */
  readonly contractPending = computed(() => {
    const v = this.vehicle();
    return !!v?.docusignSubmissionLink && v?.docusignSubmissionStatus !== 'completed';
  });
  /** Contrat signé — on expose le lien PDF. */
  readonly contractSigned = computed(() => {
    const v = this.vehicle();
    return v?.docusignSubmissionStatus === 'completed' && !!v?.provisionContract;
  });
  /** URL brute de signature Docuseal — ouverte dans un nouvel onglet via
   *  `window.open()` (l'iframe posait des problèmes de cookies/CSRF cross-
   *  origin → 422 à la soumission). Le polling de `/link-contract/{id}`
   *  détecte ensuite la complétion sans avoir besoin d'event JS de l'autre
   *  fenêtre. */
  readonly contractSignUrl = computed(() => this.vehicle()?.docusignSubmissionLink ?? null);

  /** #59 — une prolongation/modification de la fenêtre de mise à dispo est en
   *  attente de signature (la fenêtre effective courante reste valide d'ici là). */
  readonly resignaturePending = computed(() => {
    const v = this.vehicle();
    return !!(v?.pendingProvisionStartDate || v?.pendingProvisionEndDate);
  });

  readonly canPublish = computed(() => {
    const s = this.vehicle()?.status;
    return s === 'UNAVAILABLE' || s === 'UNDER_MAINTENANCE';
  });
  readonly canUnpublish = computed(() => this.vehicle()?.status === 'AVAILABLE');
  readonly canMaintain = computed(() => this.vehicle()?.status === 'AVAILABLE');
  readonly canResubmit = computed(() => this.vehicle()?.status === 'PRE_UNDER_REVIEW');
  readonly canEndMaintain = computed(() => this.vehicle()?.status === 'UNDER_MAINTENANCE');

  private id!: number;

  ngOnInit(): void {
    const idParam = this.route.snapshot.paramMap.get('id');
    const parsed = idParam ? Number(idParam) : NaN;
    if (!Number.isFinite(parsed)) {
      this.loadError.set('Identifiant invalide.');
      this.loading.set(false);
      return;
    }
    this.id = parsed;
    this.fetch();
  }

  fetch(): void {
    this.loading.set(true);
    this.loadError.set(null);
    this.vehiclesService
      .get(this.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (v) => {
          this.vehicle.set(v);
          this.loading.set(false);
          this.loadEffectivePolicy();
          this.loadDepositRate();
        },
        error: (err: unknown) => {
          this.loading.set(false);
          this.loadError.set(
            toErrorMessage(err, 'errors.vehicleLoadFailed'),
          );
        },
      });
  }

  /** Politique d'annulation qui s'applique réellement à ce véhicule (best-effort). */
  private loadEffectivePolicy(): void {
    this.policiesService
      .vehicleEffectivePolicy(this.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (p) => this.effectivePolicy.set(p),
        error: () => this.effectivePolicy.set(null),
      });
  }

  /** Taux de caution effectif du véhicule (résolu + borné, #46 — best-effort). */
  private loadDepositRate(): void {
    this.depositService
      .effectiveRate(this.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (r) => this.depositRate.set(r),
        error: () => this.depositRate.set(null),
      });
  }

  // ── Transitions de statut ─────────────────────────────────────────────
  publish(): void { this.changeStatus('AVAILABLE'); }
  unpublish(): void { this.changeStatus('UNAVAILABLE'); }
  maintain(): void { this.changeStatus('UNDER_MAINTENANCE'); }
  resubmit(): void { this.changeStatus('UNDER_REVIEW'); }

  private changeStatus(next: VehicleStatus): void {
    if (this.publishing()) return;
    this.publishing.set(true);
    this.publishError.set(null);
    this.vehiclesService
      .changeStatusMine(this.id, next)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (v) => {
          this.publishing.set(false);
          this.vehicle.set(v);
        },
        error: (err: unknown) => {
          this.publishing.set(false);
          this.publishError.set(
            toErrorMessage(err, 'errors.statusChangeFailed'),
          );
        },
      });
  }

  /** Appelé par la galerie quand l'imgCover a changé — on resync le signal. */
  onCoverChanged(_newCoverUrl: string): void {
    this.fetch();
  }

  /** Appelé après ajout/retrait d'équipements — le back retourne le véhicule
   *  à jour, on resync le signal sans nouveau GET. */
  onOptionsChanged(updated: Vehicle): void {
    this.vehicle.set(updated);
  }

  // ── Contract signing (popup Docuseal + polling /link-contract) ──────
  //
  // Stratégie : on ouvre le lien Docuseal dans un nouvel onglet pour que
  // la signature s'exécute dans un vrai contexte browser (cookies, session,
  // CSRF natifs — l'iframe cross-origin posait des 422 silencieux). Côté
  // owner-web on garde un petit dialog d'attente + on poll `/link-contract`
  // toutes les 3 s pour détecter la complétion. Bonus : on poll aussi à
  // chaque `focus` de la fenêtre owner (= retour depuis l'onglet Docuseal).

  /** Référence à la fenêtre popup pour la fermer programmatiquement quand
   *  la signature est détectée comme terminée. Peut être `null` si la popup
   *  a été bloquée par le browser (pop-up blocker). */
  private popupRef: Window | null = null;
  /** Souscription du polling — coupée par `closeSign()` ou la détection
   *  d'un statut terminal (`completed` / `declined` / `expired`). */
  private pollSub?: Subscription;
  /** Souscription au `window.focus` — vit aussi le temps de la signature. */
  private focusSub?: Subscription;

  openSign(): void {
    const url = this.contractSignUrl();
    if (!url) {
      this.signError.set('Lien de signature indisponible. Recharge la page.');
      return;
    }
    this.signError.set(null);
    this.signOpen.set(true);

    // Ouverture dans un nouvel onglet (passer "_blank" plutôt qu'un nom de
    // fenêtre pour éviter qu'un re-clic recycle l'ancienne instance avec
    // un état périmé).
    this.popupRef = window.open(url, '_blank', 'noopener,noreferrer');

    // Si le pop-up blocker est intervenu, on continue quand même : l'UI
    // expose un fallback "Ouvrir manuellement" qui re-tente l'ouverture.

    this.startSignPolling();
    this.startFocusListener();
  }

  /** Fermeture manuelle du dialog d'attente. Tente de fermer la popup et
   *  fait un dernier resync au cas où l'event de complétion aurait été raté
   *  juste avant. */
  closeSign(): void {
    this.signOpen.set(false);
    this.stopSignPolling();
    this.stopFocusListener();
    // Best-effort — `window.close()` ne marche que sur les fenêtres ouvertes
    // par script (ce qui est notre cas ici).
    try { this.popupRef?.close(); } catch { /* ignore */ }
    this.popupRef = null;
    // Un dernier resync pour capter le cas où le user a signé puis fermé
    // l'onglet juste avant le prochain tick de polling.
    this.resyncContractOnce();
  }

  /** Rouvre la popup si elle a été fermée ou bloquée — déclenché depuis
   *  un click utilisateur (les browsers exigent un geste user pour
   *  contourner un éventuel pop-up blocker). */
  reopenSignPopup(): void {
    const url = this.contractSignUrl();
    if (!url) return;
    try { this.popupRef?.close(); } catch { /* ignore */ }
    this.popupRef = window.open(url, '_blank', 'noopener,noreferrer');
  }

  /** Polling 3 s qui s'arrête sur un statut terminal Docuseal. */
  private startSignPolling(): void {
    this.stopSignPolling();
    this.pollSub = timer(
      VehicleDetailComponent.SIGN_POLL_MS,
      VehicleDetailComponent.SIGN_POLL_MS,
    )
      .pipe(
        filter(() => this.signOpen()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.pollContractStatus());
  }

  private stopSignPolling(): void {
    this.pollSub?.unsubscribe();
    this.pollSub = undefined;
  }

  /** Polle immédiatement à chaque retour de focus sur la fenêtre owner
   *  (= probable retour depuis l'onglet Docuseal). Bien plus réactif que
   *  d'attendre le tick suivant du polling périodique. */
  private startFocusListener(): void {
    this.stopFocusListener();
    this.focusSub = fromEvent(window, 'focus')
      .pipe(
        filter(() => this.signOpen()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.pollContractStatus());
  }

  private stopFocusListener(): void {
    this.focusSub?.unsubscribe();
    this.focusSub = undefined;
  }

  /** Une itération de polling — si le statut Docuseal est terminal, on
   *  ferme le dialog automatiquement. Erreur réseau silencieuse (on
   *  réessayera au tick suivant). */
  private pollContractStatus(): void {
    if (this.signRefreshing()) return; // évite les requêtes concurrentes
    this.signRefreshing.set(true);
    this.vehiclesService
      .linkContract(this.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (v) => {
          this.vehicle.set(v);
          this.signRefreshing.set(false);
          const status = v.docusignSubmissionStatus;
          if (
            status === 'completed' ||
            status === 'declined'
          ) {
            // Complétion détectée → on referme tout sans déclencher un
            // nouveau resync (déjà à jour).
            this.signOpen.set(false);
            this.stopSignPolling();
            this.stopFocusListener();
            try { this.popupRef?.close(); } catch { /* ignore */ }
            this.popupRef = null;
          }
        },
        error: () => {
          this.signRefreshing.set(false);
          // Pas de set d'erreur — le polling réessaiera. On évite de
          // spammer l'UI d'erreurs réseau transitoires.
        },
      });
  }

  /** Variante non-polled utilisée à la fermeture manuelle. Affiche une
   *  erreur en cas d'échec (contrairement au polling qui les avale). */
  private resyncContractOnce(): void {
    this.signRefreshing.set(true);
    this.vehiclesService
      .linkContract(this.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (v) => {
          this.vehicle.set(v);
          this.signRefreshing.set(false);
        },
        error: (err: unknown) => {
          this.signRefreshing.set(false);
          this.signError.set(
            toErrorMessage(err, 'errors.contractStatusFailed'),
          );
        },
      });
  }

  // ── Delete (self-service) ────────────────────────────────────────────
  openDelete(): void { this.deleteError.set(null); this.deleteOpen.set(true); }
  closeDelete(): void { if (this.deleting()) return; this.deleteOpen.set(false); }
  confirmDelete(): void {
    this.deleting.set(true);
    this.deleteError.set(null);
    this.vehiclesService
      .deleteMine(this.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.deleting.set(false);
          this.deleteOpen.set(false);
          void this.router.navigate(['/my-vehicles']);
        },
        error: (err: unknown) => {
          this.deleting.set(false);
          this.deleteError.set(toErrorMessage(err, 'errors.deleteFailed'));
        },
      });
  }
}
